import { ReactPortal, useCallback, useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  LexicalEditor,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  getDOMSelection,
  isDOMNode,
  SELECTION_CHANGE_COMMAND,
  ElementNode,
  TextNode,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableNode,
  $isTableSelection,
  getTableElement,
  getTableObserverFromTableElement,
  TableCellNode,
  TableNode,
  TableObserver,
} from '@lexical/table';
import { mergeRegister } from '@lexical/utils';
import { createPortal } from 'react-dom';

import { Box } from '../../../../Box';
import { Icon } from '../../../../Icon';
import { Typography } from '../../../../Typography';
import { MenuItem } from '../../../../MenuItem';
import { Popover } from '../../../../Popover';
import { IconButton } from '../../../../IconButton';
import { getSelectedNode } from '../../utils';

import { useDropdownButtonPosition } from './hooks';

const getTableDimensions = (
  tableNode: TableNode,
): {
  rows: number;
  columns: number;
} => {
  const columns = tableNode.getColumnCount();
  const rows = tableNode.getChildren().length;

  return {
    rows: rows,
    columns: columns,
  };
};

const currentCellBackgroundColor = (editor: LexicalEditor): null | string => {
  return editor.getEditorState().read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const [cell] = $getNodeTriplet(selection.anchor);
      if ($isTableCellNode(cell)) {
        return cell.getBackgroundColor();
      }
    }
    return null;
  });
};

const findTableNodeFromNode = (startNode: ElementNode | TextNode) => {
  let currentNode: ElementNode | TextNode | null = startNode;

  while (currentNode) {
    if ($isTableNode(currentNode)) {
      return currentNode;
    }
    currentNode = currentNode.getParent();
  }

  return null;
};

interface TableCellActionMenuProps {
  contextRef: { current: null | HTMLElement };
  onClose: () => void;
  setIsMenuOpen: (isOpen: boolean) => void;
  tableCellNode: TableCellNode;
}

const TableActionMenu = ({
  onClose,
  tableCellNode: _tableCellNode,
  setIsMenuOpen,
  contextRef,
}: TableCellActionMenuProps) => {
  const [editor] = useLexicalComposerContext();
  const theme = useTheme();
  const dropDownRef = useRef<HTMLDivElement | null>(null);

  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode);
  const [tableDimensions, setTableDimensions] = useState({
    columns: 1,
    rows: 1,
  });
  const [actionMenuAnchorEl, setActionMenuAnchorEl] =
    useState<HTMLElement | null>(contextRef.current);
  const [colorPickerAnchorEl, setColorPickerAnchorEl] =
    useState<HTMLDivElement | null>(null);

  const actionMenuOpen = Boolean(actionMenuAnchorEl);
  const colorPickerOpen = Boolean(colorPickerAnchorEl);
  const isRowsInsertDisabled = tableDimensions.rows >= 10;
  const isColumnsInsertDisabled = tableDimensions.columns >= 10;

  const ColorTypeToBlockName = {
    [theme.tokens.colors.bg.light.primary]: 'Белый',
    [theme.tokens.colors.stroke.primary]: 'Серый',
    [theme.tokens.colors.accent.stroke.blue]: 'Синий',
    [theme.tokens.colors.accent.stroke.success]: 'Зеленый',
    [theme.tokens.colors.accent.stroke.warning]: 'Оранжевый',
    [theme.tokens.colors.accent.stroke.error]: 'Красный',
    [theme.tokens.colors.accent.stroke.review]: 'Фиолетовый',
  } as const;

  const [backgroundColor, setBackgroundColor] = useState(
    () => currentCellBackgroundColor(editor) || '',
  );

  useEffect(() => {
    return editor.registerMutationListener(
      TableCellNode,
      (nodeMutations) => {
        const nodeUpdated =
          nodeMutations.get(tableCellNode.getKey()) === 'updated';

        if (nodeUpdated) {
          editor.getEditorState().read(() => {
            updateTableCellNode(tableCellNode.getLatest());
          });
          setBackgroundColor(currentCellBackgroundColor(editor) || '');
        }
      },
      { skipInitialization: true },
    );
  }, [editor, tableCellNode]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        const selectedNode = getSelectedNode(selection);
        const tableNode = findTableNodeFromNode(selectedNode);

        if (tableNode) {
          setTableDimensions(getTableDimensions(tableNode));
        }
      }
    });
  }, [editor]);

  useDropdownButtonPosition({ contextRef, dropDownRef, editor });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropDownRef.current != null &&
        contextRef.current != null &&
        isDOMNode(event.target) &&
        !dropDownRef.current.contains(event.target) &&
        !contextRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
        setActionMenuAnchorEl(null);
        setColorPickerAnchorEl(null);
      }
    };

    window.addEventListener('click', handleClickOutside);

    return () => window.removeEventListener('click', handleClickOutside);
  }, [setIsMenuOpen, contextRef]);

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        const tableElement = getTableElement(
          tableNode,
          editor.getElementByKey(tableNode.getKey()),
        );

        if (tableElement === null) {
          throw new Error('Не удалось найти таблицу');
        }

        const tableObserver = getTableObserverFromTableElement(tableElement);
        if (tableObserver !== null) {
          tableObserver.$clearHighlight();
        }

        tableNode.markDirty();
        updateTableCellNode(tableCellNode.getLatest());
      }
      $setSelection(null);
    });
  }, [editor, tableCellNode]);

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertTableRowAtSelection(shouldInsertAfter);

        onClose();
      });
    },
    [editor, onClose],
  );

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertTableColumnAtSelection(shouldInsertAfter);

        onClose();
      });
    },
    [editor, onClose],
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableRowAtSelection();
      onClose();
    });
  }, [editor, onClose]);

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      if (tableNode.isAttached() && tableNode.getColWidths() === undefined) {
        tableNode.setColWidths(
          Array.from({ length: tableNode.getColumnCount() }, () => 0),
        );
      }

      $deleteTableColumnAtSelection();

      onClose();
    });
  }, [editor, tableCellNode, onClose]);

  const handleColorPickerChange = (e: React.MouseEvent<HTMLDivElement>) => {
    if (colorPickerAnchorEl) return;

    setColorPickerAnchorEl(() => e.currentTarget);
  };

  const handleCellBackgroundColor = useCallback(
    (color: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
          const [cell] = $getNodeTriplet(selection.anchor);
          if ($isTableCellNode(cell)) {
            cell.setBackgroundColor(color);
          }

          if ($isTableSelection(selection)) {
            const nodes = selection.getNodes();

            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if ($isTableCellNode(node)) {
                node.setBackgroundColor(color);
              }
            }
          }
        }
      });
    },
    [editor],
  );

  return createPortal(
    <Popover
      open={actionMenuOpen}
      anchorEl={actionMenuAnchorEl}
      onClose={() => {
        setActionMenuAnchorEl(null);
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      ref={dropDownRef}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box paddingY="8">
        <Box onClick={handleColorPickerChange} width="100%">
          <MenuItem>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Typography
                variant="body2"
                color={
                  backgroundColor
                    ? 'original.brand.primary'
                    : 'text.light.primary'
                }
              >
                Цвет фона
              </Typography>
              <Icon name="chevron_right" size="12" />
            </Box>
          </MenuItem>
        </Box>

        <Popover
          open={colorPickerOpen}
          anchorEl={colorPickerAnchorEl}
          onClose={() => {
            setColorPickerAnchorEl(null);
          }}
          slotProps={{ paper: { style: { marginLeft: '4px' } } }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {Object.entries(ColorTypeToBlockName).map(([color, name]) => (
            <MenuItem
              onClick={() => handleCellBackgroundColor(color)}
              key={color}
            >
              <Box display="flex" alignItems="center" gap="8">
                <Box
                  width="16px"
                  height="16px"
                  radius="xs"
                  style={{
                    backgroundColor: color,
                    border: `1px solid ${
                      color === theme.tokens.colors.bg.light.primary
                        ? theme.tokens.colors.stroke.primary
                        : 'transparent'
                    }`,
                  }}
                  border="1px solid"
                />
                <Typography
                  variant="body3"
                  color={
                    backgroundColor === color
                      ? 'original.brand.primary'
                      : 'text.light.primary'
                  }
                >
                  {name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Popover>

        <Box borderBottom="1px solid" borderColor="stroke.primary" />

        <MenuItem
          disabled={isRowsInsertDisabled}
          onClick={() => insertTableRowAtSelection(false)}
        >
          <Typography variant="body2">Вставить строку выше</Typography>
        </MenuItem>
        <MenuItem
          disabled={isRowsInsertDisabled}
          onClick={() => insertTableRowAtSelection(true)}
        >
          <Typography variant="body2">Вставить строку ниже</Typography>
        </MenuItem>

        <Box borderBottom="1px solid" borderColor="stroke.primary" />

        <MenuItem
          disabled={isColumnsInsertDisabled}
          onClick={() => insertTableColumnAtSelection(false)}
        >
          <Typography variant="body2">Вставить столбец слева</Typography>
        </MenuItem>
        <MenuItem
          disabled={isColumnsInsertDisabled}
          onClick={() => insertTableColumnAtSelection(true)}
        >
          <Typography variant="body2">Вставить столбец справа</Typography>
        </MenuItem>

        <Box borderBottom="1px solid" borderColor="stroke.primary" />

        <MenuItem onClick={() => deleteTableColumnAtSelection()}>
          <Typography variant="body2">Удалить столбец</Typography>
        </MenuItem>
        <MenuItem onClick={() => deleteTableRowAtSelection()}>
          <Typography variant="body2">Удалить строку</Typography>
        </MenuItem>
        <MenuItem onClick={() => deleteTableAtSelection()}>
          <Typography variant="body2">Удалить таблицу</Typography>
        </MenuItem>
      </Box>
    </Popover>,
    document.body,
  );
};

const TableCellActionMenuContainer = ({
  anchorElem,
}: {
  anchorElem: HTMLElement;
}) => {
  const [editor] = useLexicalComposerContext();
  const menuButtonRef = useRef<HTMLDivElement | null>(null);
  const menuRootRef = useRef<HTMLButtonElement | null>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [tableCellNode, setTableMenuCellNode] = useState<TableCellNode | null>(
    null,
  );

  const $moveMenu = useCallback(() => {
    const menu = menuButtonRef.current;
    const selection = $getSelection();
    // eslint-disable-next-line no-underscore-dangle
    const nativeSelection = getDOMSelection(editor._window);
    const activeElement = document.activeElement;

    const disable = () => {
      setTableMenuCellNode(null);
    };

    if (selection == null || menu == null) {
      return disable();
    }

    const rootElement = editor.getRootElement();
    let tableObserver: TableObserver | null = null;
    let tableCellParentNodeDOM: HTMLElement | null = null;

    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      );

      if (tableCellNodeFromSelection == null) {
        return disable();
      }

      tableCellParentNodeDOM = editor.getElementByKey(
        tableCellNodeFromSelection.getKey(),
      );

      if (
        tableCellParentNodeDOM == null ||
        !tableCellNodeFromSelection.isAttached()
      ) {
        return disable();
      }

      const tableNode = $getTableNodeFromLexicalNodeOrThrow(
        tableCellNodeFromSelection,
      );
      const tableElement = getTableElement(
        tableNode,
        editor.getElementByKey(tableNode.getKey()),
      );

      if (tableElement === null) {
        throw new Error('Не удалось найти таблицу');
      }

      tableObserver = getTableObserverFromTableElement(tableElement);
      setTableMenuCellNode(tableCellNodeFromSelection);
    } else if ($isTableSelection(selection)) {
      const anchorNode = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      );

      if (!$isTableCellNode(anchorNode)) {
        throw new Error('Якорь anchorNode должен быть нодой TableCellNode');
      }
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(anchorNode);
      const tableElement = getTableElement(
        tableNode,
        editor.getElementByKey(tableNode.getKey()),
      );

      if (tableElement === null) {
        throw new Error('Не удалось найти таблицу');
      }
      tableObserver = getTableObserverFromTableElement(tableElement);
      tableCellParentNodeDOM = editor.getElementByKey(anchorNode.getKey());

      if (tableCellParentNodeDOM === null) {
        return disable();
      }
    } else if (!activeElement) {
      return disable();
    }
    if (tableObserver === null || tableCellParentNodeDOM === null) {
      return disable();
    }
    const enabled = !tableObserver || !tableObserver.isSelecting;

    if (enabled) {
      const tableCellRect = tableCellParentNodeDOM.getBoundingClientRect();
      const anchorRect = anchorElem.getBoundingClientRect();
      const top = tableCellRect.top - anchorRect.top;
      const left = tableCellRect.right - anchorRect.left;
      menu.style.transform = `translate(${left}px, ${top}px)`;
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;

    const callback = () => {
      timeoutId = undefined;
      editor.getEditorState().read($moveMenu);
    };

    const delayedCallback = () => {
      if (timeoutId === undefined) {
        timeoutId = setTimeout(callback, 0);
      }
      return false;
    };

    return mergeRegister(
      editor.registerUpdateListener(delayedCallback),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        delayedCallback,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerRootListener((rootElement, prevRootElement) => {
        if (prevRootElement) {
          prevRootElement.removeEventListener('pointerup', delayedCallback);
        }
        if (rootElement) {
          rootElement.addEventListener('pointerup', delayedCallback);
          delayedCallback();
        }
      }),
      () => clearTimeout(timeoutId),
    );
  });

  const prevTableCellDOM = useRef(tableCellNode);

  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false);
    }

    prevTableCellDOM.current = tableCellNode;
  }, [prevTableCellDOM, tableCellNode]);

  return (
    <Box position="absolute" zIndex="3" top="0" left="0" ref={menuButtonRef}>
      {tableCellNode != null && (
        <>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            ref={menuRootRef}
            position="absolute"
            right="0"
            top="0"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="24px"
            height="24px"
            bgcolor="bg.greyscale.primary"
            radius="s"
            marginRight="12"
            marginTop="16"
          >
            <IconButton size="small">
              <Icon name="chevron_down" size="16" />
            </IconButton>
          </Box>

          {isMenuOpen && (
            <TableActionMenu
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              tableCellNode={tableCellNode}
            />
          )}
        </>
      )}
    </Box>
  );
};

export const TableActionMenuPlugin = ({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): null | ReactPortal => {
  const isEditable = useLexicalEditable();

  return createPortal(
    isEditable ? (
      <TableCellActionMenuContainer anchorElem={anchorElem} />
    ) : null,
    anchorElem,
  );
};
